import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  api,
  type BillingRunAccount, type BillingRunAccountStatus, type BillingRunSummary,
} from '../lib/api';
import { fmtMonth } from '../lib/fmt';
import {
  Badge, Card, CardHeader, EmptyState, MetricCard, SectionLabel,
  SortableTh, Spinner, Table, Td, Th, Tr,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';
import { InvoiceLink } from '../components/InvoiceLink';
import { StatusBadge, fmtDatetime } from './AdminBillingRunsList';

/**
 * Billing import detail. Polls every 3 s while the run is queued/running so
 * progress updates appear without a refresh. Once the run reaches a
 * terminal state (success / warning / failed) polling stops — there's
 * nothing more to update.
 */
export default function AdminBillingRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<BillingRunSummary | null>(null);
  const [accounts, setAccounts] = useState<BillingRunAccount[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    let intervalId: number | null = null;

    async function load() {
      try {
        const { run: r, accounts: as } = await api.admin.billing.getImport(id!);
        if (!alive) return;
        setRun(r);
        setAccounts(as);
        // Stop polling on terminal state.
        if (intervalId !== null && (r.status === 'success' || r.status === 'warning' || r.status === 'failed')) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    }
    void load();
    intervalId = window.setInterval(load, 3_000);
    return () => {
      alive = false;
      if (intervalId !== null) window.clearInterval(intervalId);
    };
  }, [id]);

  if (error) return <EmptyState message={error} />;
  if (!run) return <Spinner />;

  const isLive = run.status === 'queued' || run.status === 'running';
  const durationMs = run.ended_at
    ? new Date(run.ended_at).getTime() - new Date(run.started_at).getTime()
    : Date.now() - new Date(run.started_at).getTime();

  return (
    <>
      <SectionLabel>
        <Link to="/admin/billing" style={{ color: 'var(--color-blue)' }}>← Billing</Link>
        {' / '}
        Import {fmtMonth(run.month)}
      </SectionLabel>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            Run {run.id.slice(0, 8)}… &nbsp; <StatusBadge status={run.status} />
            {isLive && (
              <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
                · live (auto-refreshing every 3s)
              </span>
            )}
          </span>
        </CardHeader>
        {run.error_message && (
          <div style={{
            padding: '10px 16px', background: 'var(--color-red-bg)',
            color: 'var(--color-red-text)', fontSize: 12,
            borderTop: '0.5px solid var(--color-border)',
          }}>
            {run.error_message}
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 10 }}>
        <MetricCard
          label="Started"
          value={fmtDatetime(run.started_at)}
          sub={`triggered by ${run.triggered_by_display_name ?? '—'}`}
        />
        <MetricCard
          label="Duration"
          value={fmtDuration(durationMs)}
          sub={run.ended_at ? `ended ${fmtDatetime(run.ended_at)}` : 'in progress'}
        />
        <MetricCard label="Created" value={run.invoices_created} accent={run.invoices_created > 0 ? 'green' : undefined} />
        <MetricCard label="Updated" value={run.invoices_updated} accent={run.invoices_updated > 0 ? 'blue' : undefined} />
        <MetricCard label="Zeroed" value={run.invoices_zeroed_out} accent={run.invoices_zeroed_out > 0 ? 'amber' : undefined} />
        <MetricCard
          label="Errors"
          value={run.accounts_errored}
          accent={run.accounts_errored > 0 ? 'amber' : undefined}
        />
      </div>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Per-account results</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            {accounts.length} row{accounts.length === 1 ? '' : 's'} ·
            invoice date {run.invoice_date} ·
            skipped {run.accounts_skipped}
          </span>
        </CardHeader>
        <AccountResultsTable accounts={accounts} />
      </Card>
    </>
  );
}

function AccountResultsTable({ accounts }: { accounts: BillingRunAccount[] }) {
  const { sorted, sort } = useTableSort(accounts, {
    account: (a: BillingRunAccount) => a.account_name ?? a.account_key,
    status:  (a: BillingRunAccount) => a.status,
    hours:   (a: BillingRunAccount) => a.total_hours,
    value:   (a: BillingRunAccount) => a.total_value,
    invoice: (a: BillingRunAccount) => a.chargeover_invoice_id,
  }, { key: 'status' });
  // Toggleable expanded row id. Errored rows are clickable; clicking again
  // (or clicking a different errored row) collapses or moves the expansion.
  // Non-error rows are non-interactive — there's no underlying detail to show.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (accounts.length === 0) {
    return <EmptyState message="No per-account rows yet — worker hasn't started processing." />;
  }
  return (
    <Table>
      <thead>
        <tr>
          <SortableTh columnKey="account" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Account</SortableTh>
          <SortableTh columnKey="status"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
          <SortableTh columnKey="hours"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Hours</SortableTh>
          <SortableTh columnKey="value"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Value</SortableTh>
          <SortableTh columnKey="invoice" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Invoice</SortableTh>
          <Th>Notes</Th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((a) => {
          const hasDetail = a.status === 'error' && (a.error_detail != null || a.error_message != null);
          const isExpanded = expandedId === a.id;
          return (
            <RowWithDetail
              key={a.id}
              account={a}
              hasDetail={hasDetail}
              isExpanded={isExpanded}
              onToggle={() => setExpandedId(isExpanded ? null : a.id)}
            />
          );
        })}
      </tbody>
    </Table>
  );
}

function RowWithDetail({
  account: a, hasDetail, isExpanded, onToggle,
}: {
  account: BillingRunAccount;
  hasDetail: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <Tr
        onClick={hasDetail ? onToggle : undefined}
        style={a.status === 'error' ? { background: 'var(--color-red-bg)' } : undefined}
      >
        <Td bold>
          {hasDetail && (
            <span style={{
              display: 'inline-block', width: 10, marginRight: 6, color: 'var(--color-text-muted)',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.1s ease',
            }}>▶</span>
          )}
          {a.account_name ?? a.account_key}
          <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 10, marginLeft: 6 }}>
            {a.account_key}
          </span>
        </Td>
        <Td><AccountStatusBadge status={a.status} errorType={a.error_type} /></Td>
        <Td right>{a.total_hours.toFixed(2)}</Td>
        <Td right>${a.total_value.toFixed(2)}</Td>
        <Td muted style={{ fontSize: 11 }}>
          <InvoiceLink invoiceId={a.chargeover_invoice_id} />
          {a.external_key && (
            <div style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>{a.external_key}</div>
          )}
        </Td>
        <Td muted style={{ fontSize: 11, color: a.error_message ? 'var(--color-red-text)' : 'var(--color-text-muted)' }}>
          {a.error_message ?? (a.processed_at ? fmtDatetime(a.processed_at) : '—')}
        </Td>
      </Tr>
      {isExpanded && hasDetail && <ErrorDetailRow account={a} />}
    </>
  );
}

function ErrorDetailRow({ account: a }: { account: BillingRunAccount }) {
  // Memoise the JSON.stringify — error_detail is stable per row and the
  // expanded view re-renders on every parent state change otherwise.
  const detailJson = useMemo(
    () => (a.error_detail != null ? JSON.stringify(a.error_detail, null, 2) : null),
    [a.error_detail],
  );
  return (
    <tr style={{ background: 'var(--color-red-bg)' }}>
      <td colSpan={6} style={{ padding: '6px 16px 14px 38px', borderBottom: '0.5px solid var(--color-border)' }}>
        {a.error_type && (
          <div style={{ marginBottom: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--color-text-muted)' }}>
            error type: <span style={{ fontFamily: 'monospace', color: 'var(--color-red-text)' }}>{a.error_type}</span>
          </div>
        )}
        {a.error_message && (
          <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--color-red-text)' }}>
            {a.error_message}
          </div>
        )}
        {detailJson && (
          <pre style={{
            margin: 0, padding: 10,
            background: 'var(--color-bg)',
            border: '0.5px solid var(--color-border)',
            borderRadius: 4,
            fontSize: 11, fontFamily: 'monospace',
            color: 'var(--color-text)',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 360, overflowY: 'auto',
          }}>
            {detailJson}
          </pre>
        )}
        {!detailJson && (
          <div style={{ fontSize: 11, color: 'var(--color-text-hint)', fontStyle: 'italic' }}>
            No structured detail captured for this error — see message above.
          </div>
        )}
      </td>
    </tr>
  );
}

function AccountStatusBadge({ status, errorType }: { status: BillingRunAccountStatus; errorType?: string | null }) {
  switch (status) {
    case 'created':    return <Badge variant="green">Created</Badge>;
    case 'updated':    return <Badge variant="blue">Updated</Badge>;
    case 'zeroed_out': return <Badge variant="amber">Zeroed</Badge>;
    case 'error':
      // Show the taxonomy tag inline so the operator can scan-filter errors
      // by category without opening every row.
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          <Badge variant="red">Error</Badge>
          {errorType && (
            <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--color-red-text)' }}>{errorType}</span>
          )}
        </span>
      );
    case 'skipped':
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          <Badge variant="gray">Skipped</Badge>
          {errorType && errorType !== 'no_billable_no_draft' && (
            <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>{errorType}</span>
          )}
        </span>
      );
    default:           return <Badge variant="gray">Pending</Badge>;
  }
}

function fmtDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}
